import { DEFAULT_TIMEOUT_MS } from "@/lib/api";

export const DEFAULT_MAX_EDI_BYTES = 51_200;

export function bytesize(str: string): number {
  return new Blob([str]).size;
}

export function csrfToken(): string {
  const meta = document.querySelector<HTMLMetaElement>(
    'meta[name="csrf-token"]',
  );
  return meta?.content ?? "";
}

// The full translation nests transaction sets inside envelope structure:
// { interchanges: [{ groups: [{ transaction_sets: [...] }] }] }. The pipeline
// splits documents and delivers each transaction set as its own bare result
// (see TransactionSetExtractorService), so tools that preview "what you get
// back from Tediware" need the flattened list.
interface EnvelopedTranslation {
  interchanges?: Array<{
    groups?: Array<{ transaction_sets?: unknown[] }>;
  }>;
}

export function extractTransactionSets(translationJson: string): unknown[] {
  let parsed: EnvelopedTranslation;
  try {
    parsed = JSON.parse(translationJson) as EnvelopedTranslation;
  } catch {
    return [];
  }
  if (!Array.isArray(parsed?.interchanges)) return [];

  return parsed.interchanges.flatMap((interchange) =>
    Array.isArray(interchange?.groups)
      ? interchange.groups.flatMap((group) =>
          Array.isArray(group?.transaction_sets) ? group.transaction_sets : [],
        )
      : [],
  );
}

export interface TranslateResult {
  // Pretty-printed JSON on success.
  json?: string;
  // User-facing error message on failure.
  error?: string;
  // Machine-readable failure, where the endpoint gave one. "rate_limited" is
  // the throttle's; callers use it to offer the way past a limit rather than
  // only reporting it.
  code?: string;
  // True when the request never reached the server (offline, DNS, etc).
  networkError?: boolean;
}

// Endpoints answer errors as a plain string, but Rack::Attack's throttled
// responder answers { error: { message, code } }. Both arrive here, and a
// caller rendering the object form would put "[object Object]" on the screen.
interface ErrorEnvelope {
  error?: string | { message?: string; code?: string };
  code?: string;
}

function readError(
  data: ErrorEnvelope | null,
  status: number,
  fallback: string,
): { error: string; code?: string } {
  const raw = data?.error;
  const structured = typeof raw === "object" && raw !== null ? raw : null;
  const plain = typeof raw === "string" ? raw : "";
  const generic =
    status === 429 ? "Too many requests. Please try again later." : fallback;

  return {
    error: structured?.message || plain || generic,
    code:
      structured?.code ??
      data?.code ??
      (status === 429 ? "rate_limited" : undefined),
  };
}

/**
 * POST EDI content to a translation endpoint and normalize the response into a
 * single result shape. extraBody is merged into the request body (e.g. a
 * Turnstile token for the public demo endpoint).
 */
export async function translateEdi(
  url: string,
  ediContent: string,
  extraBody: Record<string, unknown> = {},
): Promise<TranslateResult> {
  try {
    const res = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      body: JSON.stringify({ edi_content: ediContent, ...extraBody }),
      signal: AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
    });

    let data: ({ data?: { json?: unknown } } & ErrorEnvelope) | null = null;
    try {
      data = await res.json();
    } catch {
      /* non-JSON response */
    }

    if (!res.ok) {
      return readError(
        data,
        res.status,
        "Translation failed. Please try again.",
      );
    }

    if (data?.data?.json) {
      return { json: JSON.stringify(data.data.json, null, 2) };
    }
    return { error: "Unexpected response from the server." };
  } catch (e) {
    // A timeout (AbortSignal.timeout) is not a networkError: the request may
    // have reached the server and consumed the single-use Turnstile token
    // before the abort. Reporting networkError:false makes the caller reset the
    // widget and request a fresh token instead of reusing a spent one.
    if (e instanceof DOMException && e.name === "TimeoutError") {
      return {
        error: "The request timed out. Please try again.",
        networkError: false,
      };
    }
    return {
      error: "Network error. Please check your connection and try again.",
      networkError: true,
    };
  }
}
